import type { TFunction } from 'i18next';
import type { CandidateDocument } from '@services/drbfm.mainline';
import { apiErrorDetail, apiErrorStatus } from '@utils/apiErrorMessages';
import { fields as rowFields } from './fields';

/** Refusals the user acts on differently keep their own text (#1609, Mozy 6235180471). */
const CODE_KEYS: Readonly<Record<string, string>> = Object.assign(
  Object.create(null) as Record<string, string>,
  {
    pasted_sources_unverified: 'drbfm.firstRun.pastedUnverified',
    source_version_changed: 'drbfm.firstRun.sourceVersionChanged',
    source_not_found: 'drbfm.mainline.sourceNotFound',
    source_forbidden: 'drbfm.mainline.sourceForbidden',
    source_format_unsupported: 'drbfm.mainline.sourceFormatUnsupported',
    source_extraction_pending: 'drbfm.mainline.sourceExtractionPending',
    source_extraction_failed: 'drbfm.mainline.sourceExtractionFailed',
    source_text_empty: 'drbfm.mainline.sourceTextEmpty',
    source_too_large: 'drbfm.mainline.sourceTooLarge',
    human_only_fields_refused: 'drbfm.mainline.humanOnlyRefused',
    review_text_required: 'drbfm.mainline.reviewTextRequired',
    not_required_reason_required: 'drbfm.mainline.notRequiredReasonRequired',
    reviewed_scores_required: 'drbfm.mainline.reviewedScoresRequired',
    extraction_integrity_held: 'drbfm.mainline.extractionIntegrityHeld',
    session_not_found: 'drbfm.mainline.rowNotFound',
    design_change_not_found: 'drbfm.mainline.rowNotFound',
    failure_mode_not_found: 'drbfm.mainline.rowNotFound',
    countermeasure_not_found: 'drbfm.mainline.rowNotFound',
    // Mainline answers this as a plain string detail, which the client carries as `message`.
    analysis_in_progress: 'drbfm.mainline.analysisInProgress',
    supplement_copied_into_rows: 'drbfm.mainline.supplementCopied',
    // Mozy #1619 6249834980: a forged proposal tried to set a field only a person decides.
  },
);

// Only real row fields get a label lookup; any other name is shown as itself.
const LABELLED_FIELDS: ReadonlySet<string> = new Set(Object.values(rowFields).flat());

export function mainlineErrorMessage(t: TFunction, err: unknown): string {
  const message = (err as { message?: string }).message;
  if (message?.includes('Document text is not ready')) return t('drbfm.mainline.documentNotReady');
  if (message?.includes('Comparison input exceeds the limit')) return t('drbfm.mainline.comparisonTooLarge');
  const detail = apiErrorDetail(err);
  const detailCode = typeof detail?.code === 'string' ? detail.code : message;
  const key = typeof detailCode === 'string' ? CODE_KEYS[detailCode] : undefined;
  const fields = Array.isArray(detail?.fields)
    ? detail.fields.filter((field): field is string => typeof field === 'string')
    : [];
  if (key && (detailCode !== 'review_text_required' || fields.length > 0)) {
    const labels = fields.map(field => (LABELLED_FIELDS.has(field) ? t(`drbfm.mainline.${field}`) : field));
    return t(key, { fields: labels.join(t('drbfm.mainline.fieldSeparator')) });
  }
  const code = (err as { code?: string }).code;
  if (code === 'HTTP_503') return t('drbfm.mainline.temporarilyUnavailable');
  return t(code === 'HTTP_409' ? 'drbfm.mainline.conflict' : code === 'HTTP_422'
    ? 'drbfm.mainline.validationFailed' : 'drbfm.mainline.commandFailed');
}

export function documentEligibilityMessage(
  t: TFunction,
  doc: Pick<CandidateDocument, 'analysis_eligible' | 'analysis_refusal' | 'text_length'>,
): string {
  const key = doc.analysis_refusal ? CODE_KEYS[doc.analysis_refusal] : undefined;
  if (doc.analysis_eligible === false) return t(key ?? 'drbfm.mainline.textNotReady');
  return t(doc.text_length ? 'drbfm.mainline.textReady' : 'drbfm.mainline.textNotReady');
}

export function documentSourceErrorMessage(t: TFunction, error: unknown, phase: 'selection' | 'preview' = 'selection'): string {
  const status = apiErrorStatus(error);
  if (status === 404) return t('drbfm.firstRun.sourceMissing');
  if (status === 403) return t('drbfm.firstRun.sourceForbidden');
  if (phase === 'preview' && (error as { message?: unknown } | null)?.message === 'source_version_changed') {
    return t('drbfm.firstRun.sourcePreviewChanged');
  }
  return mainlineErrorMessage(t, error);
}
